import type { ComputedRef } from 'vue';

import { buildCourseSchema } from '~/utils/seo';

interface LearnCourseSeoMeta {
  title: () => string | undefined;
  description: () => string | undefined;
}

interface UseLearnCourseSeoOptions {
  /** The resolved resource (collection or asset). Null before it loads / when absent. */
  row: ComputedRef<{ name: string } | null>;
  /** The SEO getters `usePublicResourcePage` returns (brand-stripped title, trimmed description). */
  meta: LearnCourseSeoMeta;
  /** Cluster-prefix-free resource path, e.g. `/learn/{slug}`. */
  path: () => string;
  /** Total course workload in minutes (0/undefined omits `courseWorkload`). */
  workloadMinutes: () => number;
  /**
   * Set for routes whose parent path segment has no index page (e.g. `/learn/asset`), so that
   * segment is dropped from the breadcrumb rather than linking to a 404.
   */
  dropParentBreadcrumbSegment?: boolean;
}

/**
 * Emit the shared structured data for a public learn resource: a schema.org `Course` node plus a
 * `BreadcrumbList` (Home → Learn → resource), server-rendered so non-JS crawlers and AI engines
 * can resolve it. This is generic structured data — a Course list/carousel rich result would
 * additionally need an `ItemList` summary page (a separate follow-up), so it is not claimed here.
 * Both learn pages already hold the `row`/`meta` this consumes, so keeping the wiring in one place
 * avoids the two pages drifting.
 */
export const useLearnCourseSeo = (options: UseLearnCourseSeoOptions): void => {
  const { t } = useI18n();
  const cluster = useCurrentCluster();

  // Reaching a public resource page server-side guarantees `row.value` (a missing row 404s in
  // usePublicResourcePage), so the node is built from the resolved record. `buildCourseSchema`
  // returns null when there's no description, in which case no Course node is emitted.
  if (options.row.value) {
    const course = buildCourseSchema({
      url: `${cluster.baseUrl.value}${options.path()}`,
      name: options.meta.title() ?? options.row.value.name,
      description: options.meta.description(),
      workloadMinutes: options.workloadMinutes(),
      providerId: `${cluster.baseUrl.value}/#identity`,
    });
    if (course) {
      useSchemaOrg([course]);
    }
  }

  useBreadcrumbItems({
    overrides: () => [
      { label: t('unified.nav.home') },
      { label: t('unified.nav.learn') },
      ...(options.dropParentBreadcrumbSegment ? [false as const] : []),
      ...(options.row.value ? [{ label: options.meta.title() ?? options.row.value.name }] : []),
    ],
  });
};
